import type { ComponentProps, ReactNode } from 'react';
import { Icon, type IconName } from './Icon.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export type ButtonProps = ComponentProps<'button'> & {
  variant?: ButtonVariant;
  size?: 'md' | 'sm';
  /** full width */
  block?: boolean;
  icon?: IconName;
  /** shows a spinner and sets aria-busy; the label stays */
  busy?: boolean;
  children?: ReactNode;
};

/**
 * Pill button. primary = orange fill with near-black text (8.7:1), secondary = surface with hairline, ghost =
 * text only (accent text colour), danger = red text for destructive actions.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  block = false,
  icon,
  busy = false,
  className,
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  const cls = ['btn', `btn-${variant}`, `btn-${size}`, block ? 'btn-block' : '', className ?? '']
    .filter(Boolean)
    .join(' ');
  return (
    <button type={type} className={cls} aria-busy={busy || undefined} {...rest}>
      {busy ? (
        <span className="spinner spinner-sm" aria-hidden="true" />
      ) : (
        icon && <Icon name={icon} size="sm" />
      )}
      {children}
    </button>
  );
}

/** Opens and closes a folded part of a card: full-width row with a chevron (aria-expanded). */
export function Disclosure({
  open,
  className,
  type = 'button',
  children,
  ...rest
}: Omit<ComponentProps<'button'>, 'aria-expanded'> & { open: boolean }) {
  return (
    <button
      type={type}
      className={className ? `disclosure ${className}` : 'disclosure'}
      aria-expanded={open}
      {...rest}
    >
      <span>{children}</span>
      <Icon name="chevronDown" size="sm" />
    </button>
  );
}

/** Round icon-only button (44 px target); label is required for screen readers. */
export function IconButton({
  icon,
  label,
  className,
  type = 'button',
  ...rest
}: ComponentProps<'button'> & { icon: IconName; label: string }) {
  return (
    <button
      type={type}
      className={className ? `icon-btn ${className}` : 'icon-btn'}
      aria-label={label}
      title={label}
      {...rest}
    >
      <Icon name={icon} />
    </button>
  );
}
